import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAdminAuth } from '@/store/useAdminAuth';

// ─── Zugang zur Betriebs-API ─────────────────────────────────────────────────
//
// Zielt auf einen anderen Prozess als die Anwendung (Port 9174 statt 9173).
// Die Token beider Seiten sind nicht austauschbar: der Server prüft einen
// `scope`-Anspruch und weist Token des jeweils anderen Bereichs mit 401 ab.

const apiClient = axios.create({
  baseURL: '/api',
  timeout: 30_000,
  withCredentials: true, // Refresh-Cookie mitsenden
});

/** Der CSRF-Cookie ist bewusst nicht httpOnly – er gehört zum Double-Submit. */
function csrfAusCookie(): string | null {
  const treffer = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return treffer ? decodeURIComponent(treffer[1]) : null;
}

apiClient.interceptors.request.use((config) => {
  // Bei FormData nichts setzen: der Header braucht die von Axios erzeugte
  // boundary. (Dieselbe Falle wie in der Anwendung – dort ging der Upload
  // sonst als JSON hinaus und der Server fand keine Datei.)
  const istFormData = typeof FormData !== 'undefined' && config.data instanceof FormData;
  if (!istFormData && !config.headers['Content-Type']) {
    config.headers['Content-Type'] = 'application/json';
  }

  // Nur ergänzen, nie überschreiben: wer einen Token ausdrücklich mitgibt,
  // hat einen Grund dafür. (Beim Abmelden eines Kontos ohne Konsolenzugang
  // etwa stammt der gültige Token aus der Anmeldeantwort, während im Store
  // noch der Token der vorigen Sitzung liegt – der Server antwortete dann
  // mit 403.)
  const { accessToken, csrfToken } = useAdminAuth.getState();
  if (accessToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  if (!config.headers['X-CSRF-Token']) {
    // Vorrang hat der Cookie, nicht der Store: das Double-Submit-Verfahren
    // vergleicht Header und Cookie, der Cookie ist also die maßgebliche
    // Quelle. Überholen sich zwei Anmelde- oder Erneuerungsantworten, hält
    // der Store den Token der einen und der Browser den Cookie der anderen –
    // dann scheiterte jede schreibende Anfrage mit 403.
    const ausCookie = csrfAusCookie();
    if (ausCookie ?? csrfToken) config.headers['X-CSRF-Token'] = ausCookie ?? csrfToken;
  }

  return config;
});

// ─── Stille Erneuerung ───────────────────────────────────────────────────────
//
// Läuft das Access-Token ab (15 Minuten), wird EINMAL erneuert und die
// ursprüngliche Anfrage wiederholt. Mehrere gleichzeitige 401 teilen sich
// denselben Versuch, damit nicht jede Anfrage einen eigenen Refresh auslöst.

let laufenderRefresh: Promise<boolean> | null = null;

async function erneuern(): Promise<boolean> {
  if (!laufenderRefresh) {
    laufenderRefresh = (async () => {
      try {
        const { data } = await axios.post('/api/auth/refresh', null, {
          withCredentials: true,
        });
        useAdminAuth.getState().anmelden(data.user, data.accessToken, data.csrfToken);
        return true;
      } catch {
        useAdminAuth.getState().abmelden();
        return false;
      } finally {
        laufenderRefresh = null;
      }
    })();
  }
  return laufenderRefresh;
}

interface WiederholteAnfrage extends InternalAxiosRequestConfig {
  _wiederholt?: boolean;
}

apiClient.interceptors.response.use(
  (antwort) => antwort,
  async (fehler: AxiosError<{ message?: string; error?: string }>) => {
    const anfrage = fehler.config as WiederholteAnfrage | undefined;

    if (fehler.response?.status === 401 && anfrage && !anfrage._wiederholt) {
      // Beim Anmelden selbst nicht erneuern – sonst dreht sich das im Kreis.
      const istAnmeldung = anfrage.url?.includes('/auth/login') || anfrage.url?.includes('/auth/refresh');
      if (!istAnmeldung && (await erneuern())) {
        anfrage._wiederholt = true;
        return apiClient(anfrage);
      }
    }

    // Fehlermeldung des Servers durchreichen: sie ist für Menschen geschrieben
    // und meist genauer als alles, was der Client daraus machen würde.
    const meldung = fehler.response?.data?.message ?? fehler.message;
    return Promise.reject(new Error(meldung));
  },
);

export default apiClient;
